import { routeUrl } from "@Services/axiosShared";

/*
 * These three used to prefer a `backendUrl` / `imageUrl` / `streamServerUrl` key off the redux
 * state, read through `_.get(state, "…")`. No reducer has ever contributed those keys — the fields
 * exist on the `portals` document, not in the store — so every one of those reads was `undefined`
 * and the build-time environment variable was always what came back. The dead half is gone; the
 * environment lookup and its origin-relative fallback are unchanged.
 */

export const getBackendUrl = () => {
  if (!process.env.REACT_APP_CUBE_BACKEND) {
    return `${window.location.origin}/api-server`;
  }
  return process.env.REACT_APP_CUBE_BACKEND;
};

/**
 * The URL that streams a stored attachment's own bytes under its recorded MIME type, so it drops straight
 * into an `<img src>` or a link href; see "File uploads and downloads" in
 * packages/web/src/features/forms/CLAUDE.md. The one place it is built, on web and — through the `@Utils`
 * alias — on mobile too.
 *
 * An absent key has no URL. The second signature serves the image-answer renderers, which read the key
 * through `fileEntryAwsKey`: image and fileUpload answers share one entry type, and its File-Manager arm
 * carries no key, although only a fileUpload answer can hold such an entry.
 */
export function attachmentFileUrl(awsKey: string): string;
export function attachmentFileUrl(awsKey: string | undefined): string | undefined;
export function attachmentFileUrl(awsKey: string | undefined): string | undefined {
  return awsKey === undefined
    ? undefined
    : routeUrl(getBackendUrl(), "/fileSystemServer/getAttachmentFile/:id", { pathParams: { id: awsKey } });
}

export const getImageUrl = () => {
  if (!process.env.REACT_APP_IMAGES_PUBLIC_URL) {
    return `${window.location.origin}/assets/images`;
  }
  return process.env.REACT_APP_IMAGES_PUBLIC_URL;
};

export const getStreamServerUrl = () => {
  if (!process.env.REACT_APP_STREAMING_SERVER_PATH) {
    return `wss://${window.location.host}/stream-server`;
  }
  return process.env.REACT_APP_STREAMING_SERVER_PATH;
};

// `showSupport` is widened with `| undefined` because every caller hands over the whole
// `portalData` slice, whose server-inferred `showSupport` is `boolean | undefined`.
export const shouldShowSupport = (portalData?: { showSupport?: boolean | undefined } | null): boolean =>
  Boolean(portalData?.showSupport);

export const getSupportContactHref = (): string => `mailto:${process.env.REACT_APP_CUBE_SUPPORT_EMAIL}`;
